import { useState, type FormEvent } from "react";
import { Alert, Button, Card, InputText } from "@/deps/ui-library";

export const SET_PASSWORD_PATH = "/set-password";

const SHORTEST = 8;

/**
 * Where an invitation's link lands. The token in the link is a credential, so it's taken out of the
 * address as soon as it's read, and doesn't stay in the history or reach anything the page loads. The
 * password goes to the API's own `/bff/set-password` and on to the identity service (ADR-0003).
 */
export function SetPasswordPage() {
  const [link] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    const found = { token: query.get("token"), userId: query.get("verify") };
    window.history.replaceState(null, "", SET_PASSWORD_PATH);
    return found;
  });
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < SHORTEST) {
      setError(`Use at least ${SHORTEST} characters.`);
      return;
    }
    if (password !== again) {
      setError("Those two passwords don't match.");
      return;
    }

    setBusy(true);
    try {
      const response = await fetch("/bff/set-password", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ userId: link.userId, token: link.token, newPassword: password }),
      });
      if (response.ok) {
        setDone(true);
        return;
      }
      const problem = (await response.json().catch(() => ({}))) as {
        title?: string;
        detail?: string;
      };
      setError(problem.detail ?? problem.title ?? "That couldn't be saved. Try again in a moment.");
    } catch {
      setError("That couldn't be saved. Try again in a moment.");
    } finally {
      setBusy(false);
      setPassword("");
      setAgain("");
    }
  }

  const complete = Boolean(link.token && link.userId);

  return (
    <main className="min-h-screen bg-canvas grid place-items-center px-4">
      <Card variant="outlined" size="xl" className="w-full max-w-sm">
        <Card.Header spacing="lg" className="flex-col gap-1">
          <Card.Title as="h1" size="xl">
            {done ? "You're all set" : "Set your password"}
          </Card.Title>
          <Card.Description size="sm">
            {done
              ? "Sign in with your email and the password you just set."
              : "One account across every Rigo product."}
          </Card.Description>
        </Card.Header>
        {done ? (
          <Button colorScheme="primary" onClick={() => window.location.assign("/")}>
            Sign in
          </Button>
        ) : !complete ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger", size: "sm" }}
            description="This link isn't complete. Open it from your invitation email, or ask whoever invited you to send it again."
          />
        ) : (
          <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-3">
            {error ? (
              <Alert
                config={{ variant: "subtle", colorScheme: "danger", size: "sm" }}
                description={error}
              />
            ) : null}
            <InputText
              label="New password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <InputText
              label="Type it again"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={again}
              onChange={(e) => setAgain(e.target.value)}
            />
            <Button colorScheme="primary" type="submit" disabled={busy}>
              {busy ? "Saving" : "Set password"}
            </Button>
          </form>
        )}
      </Card>
    </main>
  );
}
